import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { listCampaignBounces } from "@/lib/bounce-log.functions";

export const Route = createFileRoute("/_authenticated/campaign-bounces")({
  component: CampaignBouncesPage,
  head: () => ({ meta: [{ title: "Bounce-Analyse pro Kampagne — InboxPulse" }] }),
});

type BounceRow = {
  id: string;
  campaign_id: string | null;
  campaign_name: string | null;
  recipient_email: string;
  recipient_display: string;
  recipient_hash: string;
  sender_email: string | null;
  bounce_type: string;
  bounce_reason: string | null;
  status_code: string | null;
  diagnostic_code: string | null;
  provider: string | null;
  received_at: string;
  message_id: string | null;
};

type CampaignGroup = {
  campaign_id: string | null;
  campaign_name: string;
  total: number;
  hard: number;
  soft: number;
  block: number;
  last_received_at: string;
  bounces: BounceRow[];
};

function CampaignBouncesPage() {
  const fetchData = useServerFn(listCampaignBounces);
  const [bounceType, setBounceType] = useState<"all" | "hard" | "soft" | "block">("all");
  const [hashRecipients, setHashRecipients] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["campaign-bounces", bounceType, hashRecipients],
    queryFn: () =>
      fetchData({
        data: {
          limit: 1000,
          bounce_type: bounceType === "all" ? undefined : bounceType,
          hash_recipients: hashRecipients,
        },
      }),
  });

  const campaigns = (data?.campaigns ?? []) as CampaignGroup[];
  const total = data?.total ?? 0;

  const summary = useMemo(() => {
    let hard = 0;
    let soft = 0;
    let block = 0;
    for (const c of campaigns) {
      hard += c.hard;
      soft += c.soft;
      block += c.block;
    }
    return { hard, soft, block };
  }, [campaigns]);

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Bounce-Analyse pro Kampagne"
        description="Gruppiert alle verifizierten Bounces nach Kampagne. Pro Empfänger werden Grund, Provider-Event-Code (SMTP-Status), Zeitpunkt und Empfänger-Adresse (klar oder gehasht) angezeigt."
      />

      <HelpPanel
        title="Hilfe: Bounces je Kampagne ansehen"
        what={
          <>
            Welche <b>Kampagne</b> hatte wie viele Bounces — und welche Empfänger waren betroffen?
          </>
        }
        why={
          <>
            Eine plötzlich hohe Bounce-Rate in EINER Kampagne ist oft das erste Zeichen: Liste
            veraltet oder Domain-Reputation gekippt.
          </>
        }
        how={[
          <>Kampagne aus der Liste wählen.</>,
          <>Tabelle zeigt jede betroffene Adresse mit Grund + SMTP-Code.</>,
          <>Roten Empfänger aus deiner aktiven Liste entfernen.</>,
        ]}
        tips={[<>Bounce-Rate über 5% → Versand pausieren, Liste säubern, dann weiter.</>]}
        glossary={[
          {
            term: "SMTP-Status-Code",
            def: "Fehlernummer vom Server (z.B. 5.1.1 = Adresse existiert nicht).",
          },
          {
            term: "Gehashte Adresse",
            def: "Verschlüsselte Anzeige der Mail-Adresse — schützt DSGVO-konform.",
          },
        ]}
      />

      <Card className="p-4 bg-card/60 border-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label>Bounce-Typ</Label>
            <Select value={bounceType} onValueChange={(v) => setBounceType(v as typeof bounceType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
                <SelectItem value="soft">Soft</SelectItem>
                <SelectItem value="block">Block</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              id="hash-recipients"
              checked={hashRecipients}
              onCheckedChange={setHashRecipients}
            />
            <Label htmlFor="hash-recipients" className="cursor-pointer">
              Empfänger pseudonymisieren (SHA-256)
            </Label>
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-2 justify-end">
            <Badge variant="outline">{total} Bounces</Badge>
            <Badge variant="destructive">Hard: {summary.hard}</Badge>
            <Badge variant="secondary">Soft: {summary.soft}</Badge>
            <Badge variant="secondary">Block: {summary.block}</Badge>
          </div>
        </div>
      </Card>

      {isLoading && (
        <Card className="p-6 text-center text-muted-foreground bg-card/60 border-border">
          Lade…
        </Card>
      )}

      {!isLoading && campaigns.length === 0 && (
        <Card className="p-6 text-center text-muted-foreground bg-card/60 border-border">
          Noch keine Bounces protokolliert.
        </Card>
      )}

      {!isLoading && campaigns.length > 0 && (
        <Card className="p-2 bg-card/60 border-border">
          <Accordion type="multiple" className="w-full">
            {campaigns.map((c) => {
              const key = c.campaign_id ?? `name:${c.campaign_name}`;
              return (
                <AccordionItem key={key} value={key} className="border-border">
                  <AccordionTrigger className="px-3 hover:no-underline">
                    <div className="flex items-center justify-between w-full pr-3 gap-3">
                      <div className="text-left">
                        <div className="font-medium">{c.campaign_name}</div>
                        <div className="text-xs text-muted-foreground">
                          Letzter Bounce: {new Date(c.last_received_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <Badge variant="outline">{c.total}</Badge>
                        {c.hard > 0 && <Badge variant="destructive">Hard {c.hard}</Badge>}
                        {c.soft > 0 && <Badge variant="secondary">Soft {c.soft}</Badge>}
                        {c.block > 0 && <Badge variant="secondary">Block {c.block}</Badge>}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-muted-foreground">
                          <tr>
                            <th className="text-left p-3">Zeit</th>
                            <th className="text-left p-3">Typ</th>
                            <th className="text-left p-3">Empfänger</th>
                            <th className="text-left p-3">Absender</th>
                            <th className="text-left p-3">Provider</th>
                            <th className="text-left p-3">Status-Code</th>
                            <th className="text-left p-3">Bounce-Grund</th>
                          </tr>
                        </thead>
                        <tbody>
                          {c.bounces.map((b) => (
                            <tr key={b.id} className="border-t border-border align-top">
                              <td className="p-3 whitespace-nowrap">
                                {new Date(b.received_at).toLocaleString()}
                              </td>
                              <td className="p-3">
                                <Badge
                                  variant={b.bounce_type === "hard" ? "destructive" : "secondary"}
                                >
                                  {b.bounce_type}
                                </Badge>
                              </td>
                              <td
                                className="p-3 font-mono text-xs break-all max-w-[260px]"
                                title={hashRecipients ? b.recipient_hash : b.recipient_email}
                              >
                                {b.recipient_display}
                              </td>
                              <td className="p-3">{b.sender_email ?? "—"}</td>
                              <td className="p-3">{b.provider ?? "—"}</td>
                              <td className="p-3 font-mono text-xs">{b.status_code ?? "—"}</td>
                              <td className="p-3 max-w-[360px]">
                                <div className="text-sm">{b.bounce_reason ?? "—"}</div>
                                {b.diagnostic_code && (
                                  <div
                                    className="mt-1 text-xs text-muted-foreground font-mono break-words line-clamp-3"
                                    title={b.diagnostic_code}
                                  >
                                    {b.diagnostic_code}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </Card>
      )}
    </div>
  );
}
